import { useState } from 'react';
import { Plus } from 'lucide-react';
import { isChecklistItemDone, localDayKey } from '@peles/shared';
import { SearchableListSelect } from '../common/SearchableListSelect';
import { TaskAssigneeChip, TaskCheckbox, TaskDueBadge } from '../tasks/TaskRow';
import { useAddItemTask, useItemTasks } from '../../hooks/usePlanning';
import { useToggleChecklistItem } from '../../hooks/useEventOps';
import { useUsers } from '../../hooks/useUsers';
import { extractApiErrorMessage } from '../../utils/apiError';

/**
 * The tasks about one item. They are ordinary tasks — on the event's Tasks
 * list, in its Items section — so they are ticked, dated and assigned like
 * any other, and the daily email and the calendar see them. Only the office
 * is offered as assignees, as on the Tasks screen.
 */
export function ItemTasksSection({ itemId }: { itemId: string }) {
  const { data: tasks = [], isLoading } = useItemTasks(itemId);
  const { data: users = [] } = useUsers({ isActive: true });
  const office = users.filter((u) => u.systemRole === 'ADMIN' || u.systemRole === 'PLANNER');
  const addTask = useAddItemTask();
  const toggle = useToggleChecklistItem();
  const [title, setTitle] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [assignee, setAssignee] = useState<string | null>(null);
  const [error, setError] = useState('');
  const today = localDayKey();

  async function add() {
    if (!title.trim()) return;
    setError('');
    try {
      await addTask.mutateAsync({
        itemId,
        title: title.trim(),
        dueDate: dueDate || null,
        assignedToUserId: assignee,
      });
      setTitle('');
      setDueDate('');
      setAssignee(null);
    } catch (err) {
      setError(extractApiErrorMessage(err, 'The task could not be added'));
    }
  }

  return (
    <section aria-label="Tasks for this item" className="border-t border-gray-100 pt-4">
      <h4 className="text-sm font-semibold text-gray-800 mb-1">Tasks for this item</h4>
      <p className="text-xs text-gray-500 mb-2">They are on the event&rsquo;s Tasks list too.</p>
      {isLoading ? (
        <p className="text-xs text-gray-400">Loading…</p>
      ) : tasks.length === 0 ? (
        <p className="text-xs text-gray-400">No tasks yet.</p>
      ) : (
        <ul className="space-y-1 mb-3">
          {tasks.map((task) => {
            const done = isChecklistItemDone(task);
            return (
              <li key={task.id} className="flex items-center gap-2 min-h-11 sm:min-h-0">
                <TaskCheckbox
                  checked={done}
                  label={`${done ? 'Reopen' : 'Complete'} ${task.title}`}
                  onChange={() => toggle.mutate({ itemId: task.id, isDone: !done })}
                />
                <span
                  className={`flex-1 text-sm ${done ? 'line-through text-gray-400' : 'text-gray-800'}`}
                >
                  {task.title}
                </span>
                {task.dueDate && !done && <TaskDueBadge dueDate={task.dueDate} todayKey={today} />}
                {task.assignedToName && <TaskAssigneeChip name={task.assignedToName} />}
              </li>
            );
          })}
        </ul>
      )}
      <div className="flex flex-wrap items-end gap-2">
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              void add();
            }
          }}
          placeholder="Add a task — confirm colours, send the deposit…"
          aria-label="New task for this item"
          className="flex-1 min-w-[180px] min-h-11 sm:min-h-0 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
        <input
          type="date"
          value={dueDate}
          onChange={(e) => setDueDate(e.target.value)}
          aria-label="Due"
          className="min-h-11 sm:min-h-0 px-3 py-2 border border-gray-300 rounded-md text-sm"
        />
        <div className="w-full sm:w-44">
          <SearchableListSelect
            items={office}
            value={assignee}
            onChange={(v) => setAssignee(typeof v === 'string' && v ? v : null)}
            getValue={(u) => u.id}
            getLabel={(u) => u.name}
            placeholder="Nobody yet"
            clearable
            ariaLabel="Assign to"
          />
        </div>
        <button
          type="button"
          onClick={() => void add()}
          disabled={!title.trim() || addTask.isPending}
          className="min-h-11 sm:min-h-9 px-3 inline-flex items-center gap-1 text-sm text-primary-700 border border-primary-300 rounded-lg hover:bg-primary-50 disabled:opacity-50"
        >
          <Plus size={14} /> Add task
        </button>
      </div>
      {error && <p className="mt-2 text-xs text-red-600">{error}</p>}
    </section>
  );
}
